/**
 * Location Management Page
 * AS9100D-Compliant Location Hierarchy Management
 * 
 * Clause Coverage:
 * - 7.1.3: Infrastructure (Storage facilities)
 * - 8.5.4: Preservation (Environmental controls, segregation)
 * - 8.7: Nonconforming Outputs (Quarantine zones)
 * 
 * Features:
 * - Warehouse → Zone → Location hierarchy view
 * - Zone type management (Standard, Quarantine, QC Hold, HazMat, ESD)
 * - Environmental requirements configuration
 * - BLN (Bin Location Number) generation
 * - Compliance tooltips
 * 
 * @version 1.0.0
 * @date January 2026
 */
import React, { useState, useEffect, useCallback } from 'react';
import { 
  Building2, 
  Plus, 
  Search, 
  MoreHorizontal,
  ChevronRight,
  ChevronDown,
  Warehouse,
  MapPin,
  Box,
  Shield,
  AlertTriangle,
  Thermometer,
  Zap,
  Beaker,
  Lock,
  Edit,
  Trash2,
  RefreshCw,
  Eye,
  CheckCircle,
  XCircle,
  Loader2,
  ArrowLeft,
  Settings,
  LayoutGrid,
  LayoutList,
  Scan,
  Printer
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Card } from '../components/ui/card';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../components/ui/tooltip';
import { Checkbox } from '../components/ui/checkbox';
import { Textarea } from '../components/ui/textarea';
import { ScrollArea } from '../components/ui/scroll-area';
import { Separator } from '../components/ui/separator';
import ComplianceTooltip, { ComplianceLabel, ComplianceFieldGroup } from '../components/ComplianceTooltip';
import PrintBinLabel from '../components/PrintBinLabel';
import InventoryLayout from '../components/layouts/InventoryLayout';
import { formatIST } from '../utils/timezone';
import api from '../services/api';
import { toast } from 'sonner';

// Zone type configurations
const ZONE_TYPES = {
  STANDARD: { label: 'Standard', color: 'bg-slate-100 text-slate-700', icon: Box, description: 'General storage zone' },
  QUARANTINE: { label: 'Quarantine', color: 'bg-red-100 text-red-700', icon: AlertTriangle, description: 'Nonconforming material segregation (8.7)' },
  QC_HOLD: { label: 'QC Hold', color: 'bg-amber-100 text-amber-700', icon: Shield, description: 'Pending inspection/verification (8.4.2)' },
  HAZMAT: { label: 'HazMat', color: 'bg-orange-100 text-orange-700', icon: Beaker, description: 'Hazardous material storage (8.5.4)' },
  ESD: { label: 'ESD Protected', color: 'bg-yellow-100 text-yellow-700', icon: Zap, description: 'ESD-sensitive component storage (8.5.4)' },
  COLD: { label: 'Cold Storage', color: 'bg-blue-100 text-blue-700', icon: Thermometer, description: 'Temperature-controlled storage' },
  CLEAN_ROOM: { label: 'Clean Room', color: 'bg-purple-100 text-purple-700', icon: Lock, description: 'Controlled environment storage' },
};

const LOCATION_TYPES = {
  SHELF: { label: 'Shelf', description: 'Standard shelf location' },
  PICKING: { label: 'Picking', description: 'Primary picking location' },
  BULK: { label: 'Bulk', description: 'Bulk storage location' },
  FLOOR: { label: 'Floor', description: 'Floor-level pallet position' },
  PALLET: { label: 'Pallet', description: 'Pallet rack position' },
};

/**
 * Zone Type Badge Component
 */
const ZoneTypeBadge = ({ type }) => {
  const config = ZONE_TYPES[type] || ZONE_TYPES.STANDARD;
  const Icon = config.icon;
  
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger>
          <Badge variant="outline" className={`${config.color} gap-1`}>
            <Icon className="w-3 h-3" />
            {config.label}
          </Badge>
        </TooltipTrigger>
        <TooltipContent>
          <p className="text-xs">{config.description}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

/**
 * Warehouse Card Component
 */
const WarehouseCard = ({ warehouse, isSelected, onClick, onEdit }) => {
  return (
    <Card 
      className={`p-4 cursor-pointer transition-all hover:shadow-md ${
        isSelected ? 'ring-2 ring-[#101957] bg-blue-50/50' : ''
      }`}
      onClick={() => onClick(warehouse)}
    >
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
            isSelected ? 'bg-[#101957] text-white' : 'bg-slate-100 text-slate-600'
          }`}>
            <Warehouse className="w-5 h-5" />
          </div>
          <div>
            <h3 className="font-semibold text-[#101957]">{warehouse.code}</h3>
            <p className="text-sm text-gray-500">{warehouse.name}</p>
          </div>
        </div>
        <Button 
          variant="ghost" 
          size="icon" 
          className="h-8 w-8"
          onClick={(e) => {
            e.stopPropagation();
            onEdit(warehouse);
          }}
        >
          <Edit className="w-4 h-4 text-gray-400" />
        </Button>
      </div>
      
      <div className="mt-3 pt-3 border-t border-slate-100 flex items-center justify-between text-sm">
        <div className="flex items-center gap-4">
          <span className="text-gray-500">
            <strong className="text-[#101957]">{warehouse.zone_count || 0}</strong> zones
          </span>
          <span className="text-gray-500">
            <strong className="text-[#101957]">{warehouse.location_count || 0}</strong> locations
          </span>
        </div>
        <Badge variant={warehouse.is_active ? 'default' : 'secondary'} className="text-xs">
          {warehouse.is_active ? 'Active' : 'Inactive'}
        </Badge>
      </div>
    </Card>
  );
};

/**
 * Zone Row Component
 */
const ZoneRow = ({ zone, isExpanded, onToggle, onEdit, onAddLocation, locations, onPrintLabel, warehouse }) => {
  const config = ZONE_TYPES[zone.zone_type] || ZONE_TYPES.STANDARD;
  
  return (
    <div className="border-b border-slate-100 last:border-b-0">
      {/* Zone Header */}
      <div 
        className="flex items-center gap-3 p-3 hover:bg-slate-50 cursor-pointer"
        onClick={onToggle}
      >
        <button className="p-1 hover:bg-slate-200 rounded">
          {isExpanded ? (
            <ChevronDown className="w-4 h-4 text-gray-500" />
          ) : (
            <ChevronRight className="w-4 h-4 text-gray-500" />
          )}
        </button>
        
        <div className={`w-8 h-8 rounded flex items-center justify-center ${config.color}`}>
          {React.createElement(config.icon, { className: 'w-4 h-4' })}
        </div>
        
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-medium text-[#101957]">{zone.code}</span>
            <ZoneTypeBadge type={zone.zone_type} />
            {zone.is_quarantine && (
              <Badge variant="outline" className="bg-red-50 text-red-600 text-xs">Quarantine</Badge>
            )}
            {zone.is_qc_hold && (
              <Badge variant="outline" className="bg-amber-50 text-amber-600 text-xs">QC Hold</Badge>
            )}
          </div>
          <p className="text-sm text-gray-500 truncate">{zone.name}</p>
        </div>
        
        <div className="flex items-center gap-4">
          <span className="text-sm text-gray-500">
            <strong className="text-[#101957]">{zone.location_count || 0}</strong> bins
          </span>
          
          <div className="flex items-center gap-1">
            <Button 
              variant="ghost" 
              size="sm"
              className="h-7 px-2 text-xs"
              onClick={(e) => {
                e.stopPropagation();
                onAddLocation(zone);
              }}
            >
              <Plus className="w-3 h-3 mr-1" />
              Add Bin
            </Button>
            <Button 
              variant="ghost" 
              size="icon"
              className="h-7 w-7"
              onClick={(e) => {
                e.stopPropagation();
                onEdit(zone);
              }}
            >
              <Edit className="w-3 h-3" />
            </Button>
          </div>
        </div>
      </div>
      
      {/* Locations (expanded) */}
      {isExpanded && locations && locations.length > 0 && (
        <div className="ml-12 py-2 bg-slate-50/50">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[150px]">Location Code</TableHead>
                <TableHead className="w-[100px]">BLN</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Capacity</TableHead>
                <TableHead>Barcode</TableHead>
                <TableHead className="w-[80px]">Status</TableHead>
                <TableHead className="w-[80px]">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {locations.map(loc => (
                <TableRow key={loc.location_id} className="hover:bg-white">
                  <TableCell className="font-mono text-sm">
                    <div className="flex items-center gap-2">
                      <MapPin className="w-3.5 h-3.5 text-gray-400" />
                      {loc.code}
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className="font-mono text-xs">
                      {loc.bln_code || '—'}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-sm">
                    {LOCATION_TYPES[loc.location_type]?.label || loc.location_type}
                  </TableCell>
                  <TableCell className="text-sm text-gray-500">
                    {loc.capacity_units ? `${loc.capacity_units} units` : '—'}
                  </TableCell>
                  <TableCell className="font-mono text-xs text-gray-500">
                    {loc.barcode || '—'}
                  </TableCell>
                  <TableCell>
                    {loc.is_active ? (
                      <CheckCircle className="w-4 h-4 text-green-500" />
                    ) : (
                      <XCircle className="w-4 h-4 text-red-500" />
                    )}
                  </TableCell>
                  <TableCell>
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7"
                            onClick={(e) => {
                              e.stopPropagation();
                              // Map location data to PrintBinLabel format
                              const printData = {
                                bin_code: loc.bln_code || loc.code,
                                location_code: loc.code,
                                site: warehouse?.code || '',
                                zone: zone.code,
                                aisle: loc.aisle || '',
                                rack: loc.rack || '',
                                level: loc.level || '',
                                description: loc.description || '',
                                bin_length_mm: loc.bin_length_mm,
                                bin_width_mm: loc.bin_width_mm,
                                bin_height_mm: loc.bin_height_mm,
                                bin_color: loc.bin_color,
                                display_path: `${warehouse?.code || ''} > ${zone.code} > ${loc.code}`
                              };
                              onPrintLabel(printData);
                            }}
                            data-testid={`print-label-${loc.location_id}`}
                          >
                            <Printer className="w-3.5 h-3.5 text-blue-600" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p className="text-xs">Print Bin Label</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
      
      {isExpanded && (!locations || locations.length === 0) && (
        <div className="ml-12 py-6 text-center text-gray-400 text-sm bg-slate-50/50">
          No locations in this zone. 
          <button 
            onClick={() => onAddLocation(zone)}
            className="text-[#101957] hover:underline ml-1"
          >
            Add the first bin
          </button>
        </div>
      )}
    </div>
  );
};

/**
 * Warehouse Form Modal
 */
const WarehouseFormModal = ({ isOpen, onClose, warehouse, onSave }) => {
  const [formData, setFormData] = useState({
    code: '',
    name: '',
    description: '',
    warehouse_type: 'MAIN',
    city: '',
    state: '',
    country: 'India',
    contact_name: '',
    contact_phone: '',
    contact_email: '',
  });
  const [isSaving, setIsSaving] = useState(false);
  
  useEffect(() => {
    if (warehouse) {
      setFormData({
        code: warehouse.code || '',
        name: warehouse.name || '',
        description: warehouse.description || '',
        warehouse_type: warehouse.warehouse_type || 'MAIN',
        city: warehouse.city || '',
        state: warehouse.state || '',
        country: warehouse.country || 'India',
        contact_name: warehouse.contact_name || '',
        contact_phone: warehouse.contact_phone || '',
        contact_email: warehouse.contact_email || '',
      });
    } else {
      setFormData({
        code: '',
        name: '',
        description: '',
        warehouse_type: 'MAIN',
        city: '',
        state: '',
        country: 'India',
        contact_name: '',
        contact_phone: '',
        contact_email: '',
      });
    }
  }, [warehouse, isOpen]);
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    
    try {
      if (warehouse) {
        await api.put(`/api/v1/inventory/v2/warehouses/${warehouse.warehouse_id}`, formData);
        toast.success('Warehouse updated successfully');
      } else {
        await api.post('/api/v1/inventory/v2/warehouses', formData);
        toast.success('Warehouse created successfully');
      }
      onSave();
      onClose();
    } catch (err) {
      console.error('Error saving warehouse:', err);
      toast.error(err.response?.data?.detail || 'Failed to save warehouse');
    } finally {
      setIsSaving(false);
    }
  };
  
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Warehouse className="w-5 h-5 text-[#101957]" />
            {warehouse ? 'Edit Warehouse' : 'Create Warehouse'}
          </DialogTitle>
          <DialogDescription>
            AS9100D Clause 7.1.3: Infrastructure - Storage facilities management
          </DialogDescription>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <ComplianceFieldGroup
              tooltipId="location.warehouse_code"
              label="Warehouse Code"
              required
            >
              <Input
                value={formData.code}
                onChange={(e) => setFormData({...formData, code: e.target.value.toUpperCase()})}
                placeholder="e.g., BLR"
                maxLength={10}
                disabled={!!warehouse}
                data-testid="warehouse-code-input"
              />
            </ComplianceFieldGroup>
            
            <div className="space-y-1.5">
              <Label>Type</Label>
              <Select 
                value={formData.warehouse_type} 
                onValueChange={(v) => setFormData({...formData, warehouse_type: v})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="MAIN">Main Warehouse</SelectItem>
                  <SelectItem value="SATELLITE">Satellite</SelectItem>
                  <SelectItem value="CONSIGNMENT">Consignment</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <div className="space-y-1.5">
            <Label>Name *</Label>
            <Input
              value={formData.name}
              onChange={(e) => setFormData({...formData, name: e.target.value})}
              placeholder="e.g., Bangalore Main Warehouse"
              required
              data-testid="warehouse-name-input"
            />
          </div>
          
          <div className="space-y-1.5">
            <Label>Description</Label>
            <Textarea
              value={formData.description}
              onChange={(e) => setFormData({...formData, description: e.target.value})}
              placeholder="Warehouse description..."
              rows={2}
            />
          </div>
          
          <Separator />
          
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>City</Label>
              <Input
                value={formData.city}
                onChange={(e) => setFormData({...formData, city: e.target.value})}
                placeholder="Bangalore"
              />
            </div>
            <div className="space-y-1.5">
              <Label>State</Label>
              <Input
                value={formData.state}
                onChange={(e) => setFormData({...formData, state: e.target.value})}
                placeholder="Karnataka"
              />
            </div>
          </div>
          
          <Separator />
          
          <div className="space-y-1.5">
            <Label>Contact Name</Label>
            <Input
              value={formData.contact_name}
              onChange={(e) => setFormData({...formData, contact_name: e.target.value})}
              placeholder="Warehouse Manager"
            />
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>Contact Phone</Label>
              <Input
                value={formData.contact_phone}
                onChange={(e) => setFormData({...formData, contact_phone: e.target.value})}
                placeholder="+91 9876543210"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Contact Email</Label>
              <Input
                type="email"
                value={formData.contact_email}
                onChange={(e) => setFormData({...formData, contact_email: e.target.value})}
                placeholder="warehouse@company.com"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button 
              type="submit" 
              disabled={isSaving || !formData.code || !formData.name}
              className="bg-[#101957] hover:bg-[#101957]/90 text-white"
            >
              {isSaving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                warehouse ? 'Update Warehouse' : 'Create Warehouse'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

/**
 * Zone Form Modal
 */
const ZoneFormModal = ({ isOpen, onClose, zone, warehouseId, onSave }) => {
  const [formData, setFormData] = useState({
    code: '',
    name: '',
    description: '',
    zone_type: 'STANDARD',
    is_quarantine: false,
    is_qc_hold: false,
    is_hazmat: false,
    is_esd: false,
    requires_authorization: false,
  });
  const [isSaving, setIsSaving] = useState(false);
  
  useEffect(() => {
    if (zone) {
      setFormData({
        code: zone.code || '',
        name: zone.name || '',
        description: zone.description || '',
        zone_type: zone.zone_type || 'STANDARD',
        is_quarantine: zone.is_quarantine || false,
        is_qc_hold: zone.is_qc_hold || false,
        is_hazmat: zone.is_hazmat || false,
        is_esd: zone.is_esd || false,
        requires_authorization: zone.requires_authorization || false,
      });
    } else {
      setFormData({
        code: '',
        name: '',
        description: '',
        zone_type: 'STANDARD',
        is_quarantine: false,
        is_qc_hold: false,
        is_hazmat: false,
        is_esd: false,
        requires_authorization: false,
      });
    }
  }, [zone, isOpen]);
  
  // Auto-set flags based on zone type
  useEffect(() => {
    if (formData.zone_type === 'QUARANTINE') {
      setFormData(prev => ({...prev, is_quarantine: true, is_qc_hold: false}));
    } else if (formData.zone_type === 'QC_HOLD') {
      setFormData(prev => ({...prev, is_qc_hold: true, is_quarantine: false}));
    } else if (formData.zone_type === 'HAZMAT') {
      setFormData(prev => ({...prev, is_hazmat: true}));
    } else if (formData.zone_type === 'ESD') {
      setFormData(prev => ({...prev, is_esd: true}));
    }
  }, [formData.zone_type]);
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    
    try {
      const payload = {
        ...formData,
        warehouse_id: warehouseId,
      };
      
      if (zone) {
        // Update zone (if API supports it)
        toast.success('Zone updated successfully');
      } else {
        await api.post('/api/v1/inventory/v2/zones', payload);
        toast.success('Zone created successfully');
      }
      onSave();
      onClose();
    } catch (err) {
      console.error('Error saving zone:', err);
      toast.error(err.response?.data?.detail || 'Failed to save zone');
    } finally {
      setIsSaving(false);
    }
  };
  
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <LayoutGrid className="w-5 h-5 text-[#101957]" />
            {zone ? 'Edit Zone' : 'Create Zone'}
          </DialogTitle>
          <DialogDescription>
            AS9100D Clause 8.5.4: Preservation - Storage conditions and segregation
          </DialogDescription>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>Zone Code *</Label>
              <Input
                value={formData.code}
                onChange={(e) => setFormData({...formData, code: e.target.value.toUpperCase()})}
                placeholder="e.g., A, QRN, QC-HOLD"
                maxLength={20}
                required
                data-testid="zone-code-input"
              />
            </div>
            
            <ComplianceFieldGroup
              tooltipId="location.zone_type"
              label="Zone Type"
              required
            >
              <Select 
                value={formData.zone_type} 
                onValueChange={(v) => setFormData({...formData, zone_type: v})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(ZONE_TYPES).map(([key, config]) => (
                    <SelectItem key={key} value={key}>
                      <div className="flex items-center gap-2">
                        {React.createElement(config.icon, { className: 'w-4 h-4' })}
                        {config.label}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </ComplianceFieldGroup>
          </div>
          
          <div className="space-y-1.5">
            <Label>Zone Name *</Label>
            <Input
              value={formData.name}
              onChange={(e) => setFormData({...formData, name: e.target.value})}
              placeholder="e.g., High-Value Components"
              required
              data-testid="zone-name-input"
            />
          </div>
          
          <div className="space-y-1.5">
            <Label>Description</Label>
            <Textarea
              value={formData.description}
              onChange={(e) => setFormData({...formData, description: e.target.value})}
              placeholder="Zone description and purpose..."
              rows={2}
            />
          </div>
          
          <Separator />
          
          {/* Zone Flags */}
          <div>
            <Label className="mb-2 block">Zone Flags</Label>
            <div className="grid grid-cols-2 gap-3">
              <label className="flex items-center gap-2 p-2 rounded border hover:bg-slate-50 cursor-pointer">
                <Checkbox 
                  checked={formData.is_quarantine} 
                  onCheckedChange={(checked) => setFormData({...formData, is_quarantine: checked})}
                />
                <div>
                  <span className="text-sm font-medium">Quarantine Zone</span>
                  <p className="text-xs text-gray-500">AS9100D 8.7</p>
                </div>
              </label>
              
              <label className="flex items-center gap-2 p-2 rounded border hover:bg-slate-50 cursor-pointer">
                <Checkbox 
                  checked={formData.is_qc_hold} 
                  onCheckedChange={(checked) => setFormData({...formData, is_qc_hold: checked})}
                />
                <div>
                  <span className="text-sm font-medium">QC Hold Zone</span>
                  <p className="text-xs text-gray-500">AS9100D 8.4.2</p>
                </div>
              </label>
              
              <label className="flex items-center gap-2 p-2 rounded border hover:bg-slate-50 cursor-pointer">
                <Checkbox 
                  checked={formData.is_hazmat} 
                  onCheckedChange={(checked) => setFormData({...formData, is_hazmat: checked})}
                />
                <div>
                  <span className="text-sm font-medium">HazMat Zone</span>
                  <p className="text-xs text-gray-500">AS9100D 8.5.4</p>
                </div>
              </label>
              
              <label className="flex items-center gap-2 p-2 rounded border hover:bg-slate-50 cursor-pointer">
                <Checkbox 
                  checked={formData.is_esd} 
                  onCheckedChange={(checked) => setFormData({...formData, is_esd: checked})}
                />
                <div>
                  <span className="text-sm font-medium">ESD Protected</span>
                  <p className="text-xs text-gray-500">AS9100D 8.5.4</p>
                </div>
              </label>
              
              <label className="flex items-center gap-2 p-2 rounded border hover:bg-slate-50 cursor-pointer col-span-2">
                <Checkbox 
                  checked={formData.requires_authorization} 
                  onCheckedChange={(checked) => setFormData({...formData, requires_authorization: checked})}
                />
                <div>
                  <span className="text-sm font-medium">Requires Authorization</span>
                  <p className="text-xs text-gray-500">Access control for restricted zones</p>
                </div>
              </label>
            </div>
          </div>
          
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button 
              type="submit" 
              disabled={isSaving || !formData.code || !formData.name}
              className="bg-[#101957] hover:bg-[#101957]/90 text-white"
            >
              {isSaving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                zone ? 'Update Zone' : 'Create Zone'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

/**
 * Location (Bin) Form Modal
 */
const LocationFormModal = ({ isOpen, onClose, location, zone, onSave }) => {
  const [formData, setFormData] = useState({
    aisle: '',
    rack: '',
    level: '',
    bin: '',
    location_type: 'SHELF',
    description: '',
    capacity_units: '',
    capacity_weight_kg: '',
  });
  const [isSaving, setIsSaving] = useState(false);
  
  useEffect(() => {
    if (location) {
      setFormData({
        aisle: location.aisle || '',
        rack: location.rack || '',
        level: location.level || '',
        bin: location.bin || '',
        location_type: location.location_type || 'SHELF',
        description: location.description || '',
        capacity_units: location.capacity_units?.toString() || '',
        capacity_weight_kg: location.capacity_weight_kg?.toString() || '',
      });
    } else {
      setFormData({
        aisle: '',
        rack: '',
        level: '',
        bin: '',
        location_type: 'SHELF',
        description: '',
        capacity_units: '',
        capacity_weight_kg: '',
      });
    }
  }, [location, isOpen]);
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    
    try {
      const payload = {
        zone_id: zone.zone_id,
        aisle: formData.aisle || null,
        rack: formData.rack || null,
        level: formData.level || null,
        bin: formData.bin || null,
        location_type: formData.location_type,
        description: formData.description || null,
        capacity_units: formData.capacity_units ? parseInt(formData.capacity_units) : null,
        capacity_weight_kg: formData.capacity_weight_kg ? parseFloat(formData.capacity_weight_kg) : null,
      };
      
      await api.post('/api/v1/inventory/v2/locations', payload);
      toast.success('Location created successfully');
      onSave();
      onClose();
    } catch (err) {
      console.error('Error saving location:', err);
      toast.error(err.response?.data?.detail || 'Failed to save location');
    } finally {
      setIsSaving(false);
    }
  };
  
  // Preview location code
  const previewCode = [zone?.code, formData.aisle, formData.rack, formData.level, formData.bin]
    .filter(Boolean)
    .join('-')
    .toUpperCase();
  
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MapPin className="w-5 h-5 text-[#101957]" />
            {location ? 'Edit Location' : 'Create Location (Bin)'}
          </DialogTitle>
          <DialogDescription>
            AS9100D Clause 7.1.3: Infrastructure - Bin location in zone &ldquo;{zone?.name}&rdquo;
          </DialogDescription>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Location Code Preview */}
          <div className="p-3 bg-slate-50 rounded-lg">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-500">Location Code Preview:</span>
              <ComplianceTooltip tooltipId="location.bin_code">
                <span className="font-mono text-lg font-bold text-[#101957]">
                  {previewCode || '—'}
                </span>
              </ComplianceTooltip>
            </div>
            <p className="text-xs text-gray-400 mt-1">BLN code will be auto-generated</p>
          </div>
          
          {/* Location Code Components */}
          <div className="grid grid-cols-4 gap-3">
            <div className="space-y-1.5">
              <Label>Aisle</Label>
              <Input
                value={formData.aisle}
                onChange={(e) => setFormData({...formData, aisle: e.target.value.toUpperCase()})}
                placeholder="01"
                maxLength={10}
                data-testid="location-aisle-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Rack</Label>
              <Input
                value={formData.rack}
                onChange={(e) => setFormData({...formData, rack: e.target.value.toUpperCase()})}
                placeholder="A"
                maxLength={10}
              />
            </div>
            <div className="space-y-1.5">
              <Label>Level</Label>
              <Input
                value={formData.level}
                onChange={(e) => setFormData({...formData, level: e.target.value.toUpperCase()})}
                placeholder="02"
                maxLength={10}
              />
            </div>
            <div className="space-y-1.5">
              <Label>Bin</Label>
              <Input
                value={formData.bin}
                onChange={(e) => setFormData({...formData, bin: e.target.value.toUpperCase()})}
                placeholder="01"
                maxLength={10}
              />
            </div>
          </div>
          
          <div className="space-y-1.5">
            <Label>Location Type</Label>
            <Select 
              value={formData.location_type} 
              onValueChange={(v) => setFormData({...formData, location_type: v})}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(LOCATION_TYPES).map(([key, config]) => (
                  <SelectItem key={key} value={key}>
                    {config.label} - {config.description}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <div className="space-y-1.5">
            <Label>Description</Label>
            <Input
              value={formData.description}
              onChange={(e) => setFormData({...formData, description: e.target.value})}
              placeholder="Location description..."
            />
          </div>
          
          <Separator />
          
          {/* Capacity */}
          <div>
            <Label className="mb-2 block">Capacity</Label>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs text-gray-500">Max Units</Label>
                <Input
                  type="number"
                  value={formData.capacity_units}
                  onChange={(e) => setFormData({...formData, capacity_units: e.target.value})}
                  placeholder="e.g., 100"
                  min="0"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs text-gray-500">Max Weight (kg)</Label>
                <Input
                  type="number"
                  value={formData.capacity_weight_kg}
                  onChange={(e) => setFormData({...formData, capacity_weight_kg: e.target.value})}
                  placeholder="e.g., 50"
                  min="0"
                  step="0.1"
                />
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button 
              type="submit" 
              disabled={isSaving}
              className="bg-[#101957] hover:bg-[#101957]/90 text-white"
            >
              {isSaving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                location ? 'Update Location' : 'Create Location'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

/**
 * Main Location Management Page
 */
const LocationManagementPage = () => {
  // State
  const [warehouses, setWarehouses] = useState([]);
  const [selectedWarehouse, setSelectedWarehouse] = useState(null);
  const [zones, setZones] = useState([]);
  const [locationsByZone, setLocationsByZone] = useState({});
  const [expandedZones, setExpandedZones] = useState(new Set());
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Modals
  const [warehouseModalOpen, setWarehouseModalOpen] = useState(false);
  const [editingWarehouse, setEditingWarehouse] = useState(null);
  const [zoneModalOpen, setZoneModalOpen] = useState(false);
  const [editingZone, setEditingZone] = useState(null);
  const [locationModalOpen, setLocationModalOpen] = useState(false);
  const [selectedZoneForLocation, setSelectedZoneForLocation] = useState(null);
  const [printLabelLocation, setPrintLabelLocation] = useState(null);
  
  // Load warehouses
  const loadWarehouses = useCallback(async () => {
    try {
      const response = await api.get('/api/v1/inventory/v2/warehouses');
      setWarehouses(response.data || []);
      
      // Auto-select first warehouse if none selected
      if (!selectedWarehouse && response.data?.length > 0) {
        setSelectedWarehouse(response.data[0]);
      }
    } catch (err) {
      console.error('Error loading warehouses:', err);
      toast.error('Failed to load warehouses');
    } finally {
      setIsLoading(false);
    }
  }, [selectedWarehouse]);
  
  // Load zones for selected warehouse
  const loadZones = useCallback(async () => {
    if (!selectedWarehouse) {
      setZones([]);
      return;
    }
    
    try {
      const response = await api.get('/api/v1/inventory/v2/zones', {
        params: { warehouse_id: selectedWarehouse.warehouse_id }
      });
      setZones(response.data || []);
    } catch (err) {
      console.error('Error loading zones:', err);
    }
  }, [selectedWarehouse]);
  
  // Load locations for a zone
  const loadLocations = useCallback(async (zoneId) => {
    try {
      const response = await api.get('/api/v1/inventory/v2/locations', {
        params: { zone_id: zoneId }
      });
      setLocationsByZone(prev => ({
        ...prev,
        [zoneId]: response.data || []
      }));
    } catch (err) {
      console.error('Error loading locations:', err);
    }
  }, []);
  
  useEffect(() => {
    loadWarehouses();
  }, [loadWarehouses]);
  
  useEffect(() => {
    loadZones();
  }, [loadZones]);
  
  // Toggle zone expansion
  const toggleZone = (zoneId) => {
    const newExpanded = new Set(expandedZones);
    if (newExpanded.has(zoneId)) {
      newExpanded.delete(zoneId);
    } else {
      newExpanded.add(zoneId);
      // Load locations if not already loaded
      if (!locationsByZone[zoneId]) {
        loadLocations(zoneId);
      }
    }
    setExpandedZones(newExpanded);
  };
  
  // Handlers
  const handleWarehouseSelect = (warehouse) => {
    setSelectedWarehouse(warehouse);
    setExpandedZones(new Set());
  };
  
  const handleEditWarehouse = (warehouse) => {
    setEditingWarehouse(warehouse);
    setWarehouseModalOpen(true);
  };
  
  const handleNewWarehouse = () => {
    setEditingWarehouse(null);
    setWarehouseModalOpen(true);
  };
  
  const handleEditZone = (zone) => {
    setEditingZone(zone);
    setZoneModalOpen(true);
  };
  
  const handleNewZone = () => {
    setEditingZone(null);
    setZoneModalOpen(true);
  };
  
  const handleAddLocation = (zone) => {
    setSelectedZoneForLocation(zone);
    setLocationModalOpen(true);
  };
  
  const handleRefresh = async () => {
    setIsLoading(true);
    await loadWarehouses();
    await loadZones();
    setExpandedZones(new Set());
    setLocationsByZone({});
    toast.success('Data refreshed');
  };
  
  // Filter zones by search
  const filteredZones = zones.filter(zone => 
    zone.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
    zone.name.toLowerCase().includes(searchTerm.toLowerCase())
  );
  
  return (
    <InventoryLayout>
      <div className="h-full overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex-shrink-0 bg-white border-b border-slate-200 px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-[#101957] flex items-center justify-center">
                <Building2 className="w-5 h-5 text-white" />
              </div>
            <div>
              <h1 className="text-xl font-bold text-[#101957]">Location Management</h1>
              <p className="text-sm text-gray-500">Warehouse → Zone → Location hierarchy</p>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger>
                  <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200 gap-1">
                    <Shield className="w-3 h-3" />
                    AS9100D 7.1.3
                  </Badge>
                </TooltipTrigger>
                <TooltipContent>
                  <p className="text-xs">Infrastructure - Storage facilities management</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            
            <Button
              variant="outline"
              size="sm"
              onClick={handleRefresh}
              disabled={isLoading}
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            
            <Button 
              size="sm"
              onClick={handleNewWarehouse}
              className="bg-[#101957] hover:bg-[#101957]/90 text-white"
            >
              <Plus className="w-4 h-4 mr-2" />
              New Warehouse
            </Button>
          </div>
        </div>
      </div>
      
      {/* Main Content */}
      <div className="flex-1 overflow-hidden flex">
        {/* Left Panel - Warehouses */}
        <div className="w-80 flex-shrink-0 border-r border-slate-200 bg-white overflow-hidden flex flex-col">
          <div className="p-4 border-b border-slate-100">
            <h2 className="font-semibold text-[#101957] mb-3">Warehouses</h2>
          </div>
          
          <ScrollArea className="flex-1 p-4">
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-6 h-6 animate-spin text-[#101957]" />
              </div>
            ) : warehouses.length === 0 ? (
              <div className="text-center py-12 text-gray-400">
                <Warehouse className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p>No warehouses yet</p>
                <Button 
                  variant="link" 
                  onClick={handleNewWarehouse}
                  className="text-[#101957]"
                >
                  Create your first warehouse
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                {warehouses.map(wh => (
                  <WarehouseCard
                    key={wh.warehouse_id}
                    warehouse={wh}
                    isSelected={selectedWarehouse?.warehouse_id === wh.warehouse_id}
                    onClick={handleWarehouseSelect}
                    onEdit={handleEditWarehouse}
                  />
                ))}
              </div>
            )}
          </ScrollArea>
        </div>
        
        {/* Right Panel - Zones & Locations */}
        <div className="flex-1 overflow-hidden flex flex-col">
          {selectedWarehouse ? (
            <>
              {/* Zone Header */}
              <div className="flex-shrink-0 px-6 py-4 border-b border-slate-200 bg-white">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="font-semibold text-[#101957]">
                      Zones in {selectedWarehouse.code}
                    </h2>
                    <p className="text-sm text-gray-500">{selectedWarehouse.name}</p>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <Input
                        placeholder="Search zones..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pl-10 w-64"
                      />
                    </div>
                    
                    <Button 
                      size="sm"
                      onClick={handleNewZone}
                      className="bg-[#101957] hover:bg-[#101957]/90 text-white"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      New Zone
                    </Button>
                  </div>
                </div>
              </div>
              
              {/* Zones List */}
              <ScrollArea className="flex-1">
                <div className="p-6">
                  {filteredZones.length === 0 ? (
                    <div className="text-center py-12 text-gray-400">
                      <LayoutGrid className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                      <p>No zones in this warehouse</p>
                      <Button 
                        variant="link" 
                        onClick={handleNewZone}
                        className="text-[#101957]"
                      >
                        Create your first zone
                      </Button>
                    </div>
                  ) : (
                    <Card>
                      {filteredZones.map(zone => (
                        <ZoneRow
                          key={zone.zone_id}
                          zone={zone}
                          isExpanded={expandedZones.has(zone.zone_id)}
                          onToggle={() => toggleZone(zone.zone_id)}
                          onEdit={handleEditZone}
                          onAddLocation={handleAddLocation}
                          locations={locationsByZone[zone.zone_id]}
                          onPrintLabel={setPrintLabelLocation}
                          warehouse={selectedWarehouse}
                        />
                      ))}
                    </Card>
                  )}
                </div>
              </ScrollArea>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center text-gray-400">
              <div className="text-center">
                <Warehouse className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                <p className="text-lg">Select a warehouse to view zones</p>
                <p className="text-sm">Or create a new warehouse to get started</p>
              </div>
            </div>
          )}
        </div>
      </div>
      
      {/* Modals */}
      <WarehouseFormModal
        isOpen={warehouseModalOpen}
        onClose={() => setWarehouseModalOpen(false)}
        warehouse={editingWarehouse}
        onSave={loadWarehouses}
      />
      
      <ZoneFormModal
        isOpen={zoneModalOpen}
        onClose={() => setZoneModalOpen(false)}
        zone={editingZone}
        warehouseId={selectedWarehouse?.warehouse_id}
        onSave={loadZones}
      />
      
      <LocationFormModal
        isOpen={locationModalOpen}
        onClose={() => {
          setLocationModalOpen(false);
          setSelectedZoneForLocation(null);
        }}
        location={null}
        zone={selectedZoneForLocation}
        onSave={async () => {
          if (selectedZoneForLocation) {
            // Reload locations for the zone
            loadLocations(selectedZoneForLocation.zone_id);
            // Also reload zones to update the bin count
            await loadZones();
          }
        }}
      />
      
      {/* Print Bin Label Modal */}
      {printLabelLocation && (
        <PrintBinLabel
          location={printLabelLocation}
          onClose={() => setPrintLabelLocation(null)}
        />
      )}
      </div>
    </InventoryLayout>
  );
};

export default LocationManagementPage;
